import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Divider,
  Stack,
  Fade,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DesktopWindowsOutlined as DesktopWindowsOutlinedIcon,
  SmartphoneOutlined as SmartphoneOutlinedIcon,
  DoneAll as DoneAllIcon,
  Autorenew,
} from "@mui/icons-material";

import { DataTable } from "../../common/dataTable/DataTable";
import { AppAlert } from "../../common/appAlert/AppAlert";
import { useIngestionFiles } from "../../../hooks/audience/useIngestionFiles";
import { useYearStatuses } from "../../../hooks/audience/useYearStatuses";
import { dataIngestionStrings, getLocalizedMonths } from "./locale";
import { initLocalization, isXlsxFile } from "../../../utils/helpers";
import { IngestionLegend } from "./IngestionLegend";
import { IngestionStats } from "./IngestionStats";

const DISPLAY_TYPE = {
  Desktop: "5e4c0316-b883-4d3c-89a6-ca712dba65de",
  Mobile: "77875e4d-8a75-4481-ba9a-2a29832a7840",
};

const MIN_YEAR = 2024;
const MIN_MONTH = 0;
const SOFT_VIOLET = "#cbb1ff";
const LIGHT_VIOLET_BG = "#f6edfe";

export function DataIngestion() {
  initLocalization(dataIngestionStrings);

  const now = new Date();
  const CURRENT_YEAR = now.getFullYear();
  const CURRENT_MONTH = now.getMonth();

  // ----- state -----
  const [displayTypeId, setDisplayTypeId] = useState(""); // GUID
  const [year, setYear] = useState(CURRENT_YEAR);
  const [selected, setSelected] = useState(
    Math.max(MIN_MONTH, Math.min(CURRENT_MONTH, 11)),
  );
  const [activeView, setActiveView] = useState("last");
  const [lastImport, setLastImport] = useState({
    start: "—",
    end: "—",
    file: "—",
    status: "—",
    message: "—",
  });
  const [pendingMonth, setPendingMonth] = useState({ y: year, m: selected });
  const [errorDismissed, setErrorDismissed] = useState(false);

  const inputRef = useRef(null);

  const {
    rows: fileRows,
    loading,
    uploading,
    error,
    refetch,
    uploadFiles,
  } = useIngestionFiles({
    year,
    monthIndex0: selected,
    displayTypeId: displayTypeId || null,
    take: 2,
    enabled: true,
  });

  const {
    getMonthStatus,
    setMonthStatus,
    monthStatuses,
    statusesError,
    setStatusesError,
    refetchYearStatuses,
  } = useYearStatuses({ year, displayTypeId: displayTypeId || null });

  const months = useMemo(
    () => getLocalizedMonths(dataIngestionStrings.getLanguage()),
    [],
  );

  // Imported Files is meant to be a record of actual imports - a failed upload attempt
  // still belongs in Last Import Summary (so the user sees what went wrong), but has
  // nothing to show in a table of imported data.
  const importedFileRows = useMemo(
    () => fileRows.filter((r) => r.status !== "Error"),
    [fileRows],
  );

  const fileColumns = [
    {
      field: "fileName",
      headerName: dataIngestionStrings.table.fileName,
      minWidth: 200,
      visible: true,
    },
    {
      // Imported Files is shared between Desktop and Mobile (1 file each per month),
      // so this column is what tells the two rows apart.
      field: "displayType",
      headerName: dataIngestionStrings.table.displayType,
      minWidth: 130,
      visible: true,
    },
    {
      field: "totalLines",
      headerName: dataIngestionStrings.table.noOfLines,
      minWidth: 140,
      visible: true,
    },
    {
      field: "ingestedLines",
      headerName: dataIngestionStrings.table.ingestedLines,
      minWidth: 160,
      visible: true,
    },
    {
      field: "status",
      headerName: dataIngestionStrings.table.status,
      minWidth: 120,
      visible: true,
    },
    {
      field: "message",
      headerName: dataIngestionStrings.table.message,
      minWidth: 240,
      visible: true,
    },
    {
      field: "startDate",
      headerName: dataIngestionStrings.table.startDate,
      minWidth: 220,
      visible: true,
    },
    {
      field: "endDate",
      headerName: dataIngestionStrings.table.endDate,
      minWidth: 220,
      visible: true,
    },
  ];

  const isMonthDisabled = (y, m) => {
    if (y < MIN_YEAR || (y === MIN_YEAR && m < MIN_MONTH)) return true;
    if (y > CURRENT_YEAR) return true;
    if (y === CURRENT_YEAR && m > CURRENT_MONTH) return true;
    return false;
  };

  const clampSelectedForYear = (y, sel) => {
    if (y === MIN_YEAR && sel < MIN_MONTH) return MIN_MONTH;
    if (y === CURRENT_YEAR && sel > CURRENT_MONTH) return CURRENT_MONTH;
    return sel;
  };

  const handlePrevYear = () => {
    const target = year - 1;
    if (target < MIN_YEAR) return;
    setYear(target);
    setSelected((s) => clampSelectedForYear(target, s));
  };

  const handleNextYear = () => {
    const target = year + 1;
    if (target > CURRENT_YEAR) return;
    setYear(target);
    setSelected((s) => clampSelectedForYear(target, s));
  };

  const prevYearDisabled = year <= MIN_YEAR;
  const nextYearDisabled = year >= CURRENT_YEAR;

  // Clic gauche : sélectionne le mois
  const handleSelectMonth = (mIndex) => {
    if (isMonthDisabled(year, mIndex)) return;
    setSelected(mIndex);
  };

  // Clic droit : ouvre l'explorateur de fichiers
  const handleRightClickMonth = (e, mIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (isMonthDisabled(year, mIndex)) return;
    if (getMonthStatus(year, mIndex) === "loading") return;

    if (!displayTypeId) {
      setMonthStatus(year, mIndex, "error");
      setActiveView("last");
      setLastImport({
        start: "—",
        end: "—",
        file: "—",
        status: "Error",
        message: dataIngestionStrings.errors.selectDisplayTypeFirst,
      });
      return;
    }

    // Another month for this display type is still processing in the
    // background: block starting a second upload until it clears, instead
    // of letting two Task.Run background jobs race each other.
    const hasOtherMonthInProgress = months.some(
      (_m, i) => i !== mIndex && getMonthStatus(year, i) === "loading",
    );
    if (hasOtherMonthInProgress) {
      setActiveView("last");
      setLastImport({
        start: "—",
        end: "—",
        file: "—",
        status: "Error",
        message: dataIngestionStrings.errors.anotherUploadInProgress,
      });
      return;
    }

    setSelected(mIndex);
    setPendingMonth({ y: year, m: mIndex });

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.click();
      }
    }, 0);
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    const { y, m } = pendingMonth;

    if (!files.length) return;

    if (files.some((f) => !isXlsxFile(f))) {
      setMonthStatus(y, m, "error");
      setActiveView("last");
      setLastImport({
        start: "—",
        end: "—",
        file: files.map((f) => f.name).join(", "),
        status: "Error",
        message: dataIngestionStrings.errors.notXlsx,
      });
      return;
    }

    setMonthStatus(y, m, "loading");

    try {
      // uploadFiles only waits for the POST to be accepted - the actual
      // parsing/insertion keeps running in the background on the API side.
      // Do NOT optimistically flip this month to "done" here: the refetch
      // right below reads the real (still "In Progress") status from the
      // API, so an optimistic "done" set here only produces a green flash
      // that immediately snaps back to orange - never actually reflecting
      // when the file is really finished. Let the refetch (and the polling
      // effect below, while this month is still "In Progress") be the only
      // source of truth for status/lastImport.
      await uploadFiles(files);

      setActiveView("files");

      await refetch();
      await refetchYearStatuses();
    } catch (err) {
      // Don't guess this month's status here (e.g. force it to "error"): a rejected
      // upload - already at the 2-file cap, another one already in progress, etc. -
      // doesn't mean the existing file(s) for this month are bad. The error itself is
      // already shown via the `error` alert above (from useIngestionFiles); just
      // resync the chip/summary with whatever is actually true in the database.
      console.error(err);
      await refetch();
      await refetchYearStatuses();
    }
  };

  const getMonthColor = (y, m) => {
    const status = getMonthStatus(y, m);
    if (status === "done") return "success";
    if (status === "loading") return "warning";
    if (status === "error") return "error";
    return "default";
  };

  // monthStatuses holds entries for every year/display type visited this
  // session - only count the ones for what's currently on screen.
  const currentYearStatuses = Object.entries(monthStatuses)
    .filter(([key]) => key.startsWith(`${displayTypeId}-${year}-`))
    .map(([, status]) => status);

  const finishedCount = currentYearStatuses.filter((s) => s === "done").length;
  const inProgressCount = currentYearStatuses.filter(
    (s) => s === "loading",
  ).length;
  const errorCount = currentYearStatuses.filter((s) => s === "error").length;

  // ----- effects -----
  useEffect(() => {
    if (!fileRows?.length) {
      // A month/display type with no files at all (never uploaded) must reset
      // to the empty placeholders - otherwise this keeps showing whatever the
      // previously selected month left behind.
      setLastImport({
        start: "—",
        end: "—",
        file: "—",
        status: "—",
        message: "—",
      });
      return;
    }

    const top = fileRows[0];
    setLastImport({
      file: top.fileName ?? "—",
      status: top.status ?? "—",
      message: top.message ?? "—",
      start: top.startDate ?? "—",
      end: top.endDate ?? "—",
    });
  }, [fileRows]);

  useEffect(() => {
    setErrorDismissed(false);
  }, [error]);

  // While a file is genuinely still processing in the background (Task.Run
  // on the API side), nothing pushes an update to this screen on its own -
  // there's no other trigger to notice it flipped to Completed/Error, so
  // without this the chip (and Last Import Summary) would stay stuck on
  // "loading" forever until the user manually hits refresh.
  useEffect(() => {
    if (inProgressCount === 0) return undefined;

    const interval = setInterval(() => {
      refetch();
      refetchYearStatuses();
    }, 3000);

    return () => clearInterval(interval);
  }, [inProgressCount, refetch, refetchYearStatuses]);

  return (
    <Paper sx={{ p: 2, height: "100%", position: "relative" }}>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        onChange={handleFilesSelected}
        style={{ display: "none" }}
      />

      <Grid
        container
        spacing={2}
        sx={{ height: "100%", alignContent: "flex-start" }}
      >
        <Grid xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "black" }}>
              {dataIngestionStrings.title}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {(loading || uploading) && <CircularProgress size={18} />}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  refetch();
                  refetchYearStatuses();
                }}
                disabled={!displayTypeId}
              >
                {dataIngestionStrings.refresh}
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid xs={12} sx={{ mt: 2, ml: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "220px 1fr 220px",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel id="display-type-label">
                  {dataIngestionStrings.displayType}
                </InputLabel>
                <Select
                  labelId="display-type-label"
                  value={displayTypeId}
                  onChange={(e) => setDisplayTypeId(e.target.value)}
                  input={
                    <OutlinedInput label={dataIngestionStrings.displayType} />
                  }
                  displayEmpty
                  renderValue={(val) => {
                    if (!val) return dataIngestionStrings.displayType;
                    const label =
                      val === DISPLAY_TYPE.Desktop
                        ? dataIngestionStrings.desktop
                        : dataIngestionStrings.mobile;
                    return (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {val === DISPLAY_TYPE.Desktop ? (
                          <DesktopWindowsOutlinedIcon fontSize="small" />
                        ) : (
                          <SmartphoneOutlinedIcon fontSize="small" />
                        )}
                        <span>{label}</span>
                      </Box>
                    );
                  }}
                >
                  <MenuItem value={DISPLAY_TYPE.Desktop}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DesktopWindowsOutlinedIcon fontSize="small" />
                      <span>{dataIngestionStrings.desktop}</span>
                    </Box>
                  </MenuItem>

                  <MenuItem value={DISPLAY_TYPE.Mobile}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SmartphoneOutlinedIcon fontSize="small" />
                      <span>{dataIngestionStrings.mobile}</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton
                  onClick={handlePrevYear}
                  disabled={prevYearDisabled}
                  aria-label="Previous year"
                >
                  <ChevronLeftIcon sx={{ fontSize: 22 }} />
                </IconButton>
                <Chip
                  variant="outlined"
                  label={year}
                  sx={{
                    width: 120,
                    fontSize: "1rem",
                    fontWeight: 700,
                    height: 36,
                    borderColor: SOFT_VIOLET,
                    borderWidth: 2,
                  }}
                />
                <IconButton
                  onClick={handleNextYear}
                  disabled={nextYearDisabled}
                  aria-label="Next year"
                >
                  <ChevronRightIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Stack>
            </Box>
            <Box /> {/* spacer droit */}
          </Box>

          <AppAlert
            open={!!error && !errorDismissed}
            type="error"
            onClose={() => setErrorDismissed(true)}
          >
            {error?.response?.data?.message ||
              error?.message ||
              dataIngestionStrings.errors.apiGeneric}
          </AppAlert>

          <AppAlert
            open={!!statusesError}
            type="error"
            onClose={() => setStatusesError("")}
          >
            {statusesError}
          </AppAlert>

          {/* ======= Mois + Stats ======= */}
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              gap: 1.5,
              py: 1,
              pr: 1,
              overflowX: "auto",
            }}
          >
            {/* Mois */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ flex: "1 1 auto", minWidth: 0, alignItems: "center" }}
            >
              {months.map((m, i) => {
                const disabled = isMonthDisabled(year, i);
                const isSelected = !disabled && i === selected;
                return (
                  <Chip
                    key={m}
                    size="large"
                    color={disabled ? "default" : getMonthColor(year, i)}
                    label={m}
                    disabled={disabled}
                    onClick={() => handleSelectMonth(i)}
                    onContextMenu={(e) => handleRightClickMonth(e, i)}
                    icon={
                      !disabled && getMonthStatus(year, i) === "loading" ? (
                        <Autorenew
                          sx={{
                            fontSize: "14px !important",
                            animation: "spin 1s linear infinite",
                            "@keyframes spin": {
                              "0%": { transform: "rotate(0deg)" },
                              "100%": { transform: "rotate(360deg)" },
                            },
                          }}
                        />
                      ) : undefined
                    }
                    sx={{
                      flex: "1 1 0",
                      minWidth: 44,
                      height: 40,
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      ...(isSelected && {
                        outline: `2px solid ${SOFT_VIOLET}`,
                        outlineOffset: "1px",
                      }),
                    }}
                  />
                );
              })}
            </Stack>

            <IngestionStats
              strings={dataIngestionStrings}
              finishedCount={finishedCount}
              inProgressCount={inProgressCount}
              errorCount={errorCount}
            />

            <IngestionLegend strings={dataIngestionStrings} />
          </Box>

          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2 }}
          >
            <Button
              variant={activeView === "last" ? "contained" : "outlined"}
              disableElevation
              onClick={() => setActiveView("last")}
              sx={
                activeView === "last"
                  ? {
                      backgroundColor: LIGHT_VIOLET_BG,
                      color: "#000",
                      border: "1px solid #6a4fa0",
                      "&:hover": { backgroundColor: "#ead9fc" },
                    }
                  : {}
              }
            >
              {dataIngestionStrings.lastImportBtn}
            </Button>

            <Button
              variant={activeView === "files" ? "contained" : "outlined"}
              disableElevation
              onClick={() => setActiveView("files")}
              disabled={!displayTypeId}
              sx={
                activeView === "files"
                  ? {
                      backgroundColor: LIGHT_VIOLET_BG,
                      color: "#000",
                      border: "1px solid #6a4fa0",
                      "&:hover": { backgroundColor: "#ead9fc" },
                    }
                  : {}
              }
            >
              {dataIngestionStrings.importedFilesBtn}
            </Button>
          </Box>

          <Box sx={{ mt: 3, position: "relative", minHeight: 280 }}>
            <Fade in={activeView === "last"} mountOnEnter unmountOnExit>
              <Box sx={{ position: "absolute", inset: 0 }}>
                <Paper
                  elevation={2}
                  sx={{
                    height: "100%",
                    borderLeft: "6px solid",
                    borderLeftColor:
                      lastImport.status === "Done"
                        ? "success.main"
                        : lastImport.status === "Error"
                        ? "error.main"
                        : SOFT_VIOLET,
                    backgroundColor: LIGHT_VIOLET_BG,
                    p: 2,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mb={1.5}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {dataIngestionStrings.lastImportSummary}
                    </Typography>
                    <Chip
                      size="small"
                      icon={<DoneAllIcon />}
                      color={
                        lastImport.status === "Done" ? "success" : "default"
                      }
                      label={lastImport.status}
                    />
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5}>
                    <Typography variant="body2" fontSize="0.95rem">
                      <b>{dataIngestionStrings.fields.startExecutionDate}:</b>{" "}
                      {lastImport.start}
                    </Typography>
                    <Typography variant="body2" fontSize="0.95rem">
                      <b>{dataIngestionStrings.fields.endExecutionDate}:</b>{" "}
                      {lastImport.end}
                    </Typography>
                    <Typography variant="body2" fontSize="0.95rem">
                      <b>{dataIngestionStrings.fields.fileName}:</b>{" "}
                      {lastImport.file}
                    </Typography>
                    <Typography variant="body2" fontSize="0.95rem">
                      <b>{dataIngestionStrings.fields.status}:</b>{" "}
                      {lastImport.status}
                    </Typography>
                    <Typography variant="body2" fontSize="0.95rem">
                      <b>{dataIngestionStrings.fields.message}:</b>{" "}
                      {lastImport.message}
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
            </Fade>

            <Fade in={activeView === "files"} mountOnEnter unmountOnExit>
              <Box sx={{ position: "absolute", inset: 0 }}>
                <DataTable
                  columns={fileColumns}
                  rows={importedFileRows}
                  showPagination={false}
                  showCheckboxes={false}
                  allowSelection={false}
                  tableMinWidth={1000}
                />
              </Box>
            </Fade>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
